import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Backdrop,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Fab,
  IconButton,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ForumIcon from '@material-ui/icons/Forum';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import SendIcon from '@material-ui/icons/Send';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import MicIcon from '@material-ui/icons/Mic';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { toast } from 'react-toastify';

import api from '../../services/api';
import toastError from '../../errors/toastError';
import { AuthContext } from '../../context/Auth/AuthContext';
import { UsersFilter } from '../UsersFilter';
import { useDate } from '../../hooks/useDate';
import { useInternalChatVoiceRecorder } from '../../hooks/useInternalChatVoiceRecorder';
import { i18n } from '../../translate/i18n';
import {
  getInternalChatDeleteMessagePreview,
  getInternalChatMediaKind,
  stripInternalChatMessageCaption,
} from '../../utils/internalChatMedia';
import {
  getInternalChatUnreadForUser,
  isInternalChatOwner,
  sumInternalChatUnreadsForUser,
  upsertInternalChatInList,
} from '../../utils/internalChatSync';
import { playInternalChatNotificationSound } from '../../utils/notificationSound';
import { ChatModal } from '../../pages/Chat/index';
import ConfirmationModal from '../ConfirmationModal';
import InternalChatAttachmentPreviewModal from '../InternalChatAttachmentPreviewModal';
import InternalChatMessageMedia from '../InternalChatMessageMedia';
import InternalChatVoiceNote from '../InternalChatVoiceNote';
import DeleteConfirmModal from '../MessageActionsModal/DeleteConfirmModal';

function formatRecordingClock(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  const z = (n) => (n < 10 ? `0${n}` : `${n}`);
  return `${z(m)}:${z(s)}`;
}

const useStyles = makeStyles((theme) => ({
  /** Atrás do painel e do FAB: clique fora fecha o widget */
  widgetBackdrop: {
    zIndex: 1299,
    backgroundColor: alpha(theme.palette.common.black, theme.palette.type === 'dark' ? 0.35 : 0.12),
  },
  fab: {
    position: 'fixed',
    right: 20,
    bottom: 84,
    zIndex: 1300,
    overflow: 'visible',
    transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
    [theme.breakpoints.down('sm')]: {
      right: 14,
      bottom: 78,
    },
  },
  fabClosed: {
    boxShadow: theme.shadows[8],
  },
  fabOpen: {
    backgroundColor: `${theme.palette.grey[200]} !important`,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[6],
    '&:hover': {
      backgroundColor: `${theme.palette.grey[300]} !important`,
    },
  },
  /** Badge absoluto dentro do próprio FAB para não “soltar” no layout */
  fabUnreadBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    zIndex: 1,
    display: 'grid',
    placeItems: 'center',
    minWidth: 18,
    height: 18,
    fontSize: '0.65rem',
    fontWeight: 700,
    lineHeight: 1,
    borderRadius: 9,
    boxSizing: 'border-box',
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.02em',
    pointerEvents: 'none',
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    boxShadow: '0 1px 4px rgba(15, 23, 42, 0.28)',
    border: `2px solid ${theme.palette.secondary.main}`,
  },
  /** Painel flutuante alinhado ao `dialogPaper` / modais do sistema */
  panelRoot: {
    position: 'fixed',
    right: 20,
    bottom: 150,
    width: 400,
    height: 580,
    zIndex: 1300,
    borderRadius: 16,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    boxSizing: 'border-box',
    border: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    boxShadow:
      theme.palette.type === 'dark' ? theme.shadows[24] : '0 8px 32px rgba(15, 23, 42, 0.14)',
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('sm')]: {
      right: 10,
      left: 10,
      bottom: 140,
      width: 'auto',
      height: 'calc(100vh - 200px)',
      minHeight: 440,
    },
  },
  hubHeader: {
    padding: theme.spacing(2, 2.25),
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },
  hubHeaderTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.success.main,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.25)}`,
    flexShrink: 0,
  },
  hubKicker: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: '1rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    letterSpacing: '0.01em',
  },
  hubHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  iconOnHeader: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
    },
  },
  threadHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 10px 10px 4px',
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },
  threadHeaderText: {
    flex: 1,
    minWidth: 0,
  },
  threadTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  threadSubtitle: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.35,
    marginTop: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  searchRow: {
    padding: theme.spacing(1.25, 2),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f8fafc',
    flexShrink: 0,
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.paper,
      borderRadius: 10,
      fontSize: '0.875rem',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.type === 'dark' ? theme.palette.divider : '#e2e8f0',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: 1,
    },
  },
  listArea: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f8fafc',
    ...theme.scrollbarStyles,
  },
  chatRowOuter: {
    display: 'flex',
    alignItems: 'stretch',
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  },
  chatRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '14px 16px',
    cursor: 'pointer',
    flex: 1,
    minWidth: 0,
    border: 'none',
    background: 'transparent',
    font: 'inherit',
    textAlign: 'left',
  },
  chatRowSelected: {
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
    boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
  },
  chatRowActions: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flexShrink: 0,
    padding: '6px 8px 6px 0',
    gap: 0,
  },
  chatRowActionBtn: {
    padding: 6,
    borderRadius: 8,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
      color: theme.palette.primary.main,
    },
  },
  chatRowActionBtnDanger: {
    '&:hover': {
      backgroundColor: alpha(theme.palette.error.main, 0.08),
      color: theme.palette.error.main,
    },
  },
  chatRowAvatar: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: alpha(theme.palette.primary.main, 0.14),
    color: theme.palette.primary.main,
    '& svg': {
      fontSize: 24,
    },
  },
  chatRowBody: {
    flex: 1,
    minWidth: 0,
  },
  chatRowTitle: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.25,
    marginBottom: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chatRowPreview: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.35,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chatRowMeta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 6,
    flexShrink: 0,
    paddingTop: 2,
  },
  chatRowDate: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  unreadCount: {
    boxSizing: 'border-box',
    minWidth: 22,
    height: 22,
    padding: '0 6px',
    borderRadius: 11,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 800,
    display: 'inline-grid',
    placeItems: 'center',
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.01em',
  },
  /** Padding no pai evita overflow: `fullWidth` + margin horizontal cortava a direita */
  newChatCtaWrap: {
    flexShrink: 0,
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 2, 2),
  },
  /** CTA único para nova conversa — estilo outlined alinhado a modais (sem fill verde) */
  newChatCta: {
    width: '100%',
    margin: 0,
    borderRadius: 10,
    padding: theme.spacing(1.25, 2),
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    boxShadow: 'none',
    borderWidth: 1,
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      boxShadow: 'none',
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
  },
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '14px 14px 18px',
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#eef2f7',
    fontFamily: theme.typography.fontFamily,
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  messageLeft: {
    maxWidth: 'min(92%, 100%)',
    padding: '10px 14px',
    borderRadius: 14,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e2e8f0'}`,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    fontFamily: theme.typography.fontFamily,
  },
  messageRight: {
    maxWidth: 'min(92%, 100%)',
    padding: '10px 14px',
    borderRadius: 14,
    backgroundColor: theme.palette.type === 'dark' ? '#1e3a2f' : '#d9fdd3',
    color: theme.palette.text.primary,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    fontFamily: theme.typography.fontFamily,
  },
  messageSenderName: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: theme.palette.primary.main,
    marginBottom: 4,
    display: 'block',
    fontFamily: theme.typography.fontFamily,
  },
  messageBody: {
    fontSize: '0.9375rem',
    lineHeight: 1.5,
    letterSpacing: '-0.011em',
    fontWeight: 400,
    color: 'inherit',
    fontFamily: theme.typography.fontFamily,
    '& p': { margin: 0 },
  },
  messageCaptionPlain: {
    fontSize: '0.9375rem',
    lineHeight: 1.5,
    letterSpacing: '-0.011em',
    fontWeight: 400,
    color: 'inherit',
    marginBottom: 4,
    display: 'block',
    fontFamily: theme.typography.fontFamily,
  },
  messageTime: {
    fontSize: '0.6875rem',
    fontWeight: 500,
    letterSpacing: '0.01em',
    color: theme.palette.text.secondary,
    display: 'block',
    textAlign: 'right',
    marginTop: 4,
    fontFamily: theme.typography.fontFamily,
  },
  messageTimeRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: 4,
  },
  messageDeleteIcon: {
    padding: 4,
    marginLeft: 2,
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.error.main,
      backgroundColor: alpha(theme.palette.error.main, 0.08),
    },
  },
  messageStack: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 'min(92%, 100%)',
    minWidth: 0,
    marginBottom: 10,
  },
  messageStackMine: {
    alignItems: 'flex-end',
    marginLeft: 'auto',
  },
  messageStackTheirs: {
    alignItems: 'flex-start',
  },
  senderAboveVoice: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: theme.palette.primary.main,
    marginBottom: 4,
    alignSelf: 'flex-start',
    fontFamily: theme.typography.fontFamily,
  },
  timeBelowVoice: {
    fontSize: '0.6875rem',
    fontWeight: 500,
    letterSpacing: '0.01em',
    color: theme.palette.text.secondary,
    marginTop: 4,
    lineHeight: 1.2,
    maxWidth: 300,
    fontFamily: theme.typography.fontFamily,
  },
  timeBelowVoiceMine: {
    alignSelf: 'flex-end',
    textAlign: 'right',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  timeBelowVoiceTheirs: {
    alignSelf: 'flex-start',
    textAlign: 'left',
  },
  composer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,
    padding: theme.spacing(1.5, 2, 2),
    borderTop: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },
  composerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
  },
  '@keyframes internalChatWidgetMicPulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.55 },
  },
  composerInputShell: {
    flex: 1,
    minWidth: 0,
    borderRadius: 10,
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#fff',
    border: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e2e8f0'}`,
  },
  composerTextField: {
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      backgroundColor: 'transparent',
      alignItems: 'flex-start',
      color: theme.palette.text.primary,
    },
    '& .MuiOutlinedInput-input': {
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
      fontSize: '0.9375rem',
      lineHeight: 1.5,
      letterSpacing: '-0.011em',
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
  recordingChronoOutside: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    padding: '4px 2px',
    marginRight: 2,
  },
  recordingDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: '#f87171',
    flexShrink: 0,
    animation: '$internalChatWidgetMicPulse 1s ease-in-out infinite',
  },
  recordingTime: {
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
  },
  recorderActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    gap: 2,
    minHeight: 40,
  },
  composerIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    flexShrink: 0,
    boxSizing: 'border-box',
    lineHeight: 0,
  },
  actionIconMuted: {
    color: theme.palette.text.secondary,
  },
  cancelRecordIcon: {
    color: '#f87171',
  },
  confirmRecordIcon: {
    color: '#4ade80',
  },
  chatTitle: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  emptyState: {
    padding: 24,
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
}));

const useCreateChatDialogStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    overflow: 'hidden',
    maxHeight: 'min(90vh, 640px)',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    boxShadow:
      theme.palette.type === 'dark' ? theme.shadows[24] : '0 8px 32px rgba(15, 23, 42, 0.14)',
    backgroundColor: theme.palette.background.paper,
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 2.25),
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    '& svg': {
      fontSize: 22,
      color: theme.palette.primary.main,
    },
  },
  headerTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
    lineHeight: 1.35,
  },
  headerCloseBtn: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
    },
  },
  dialogContent: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(2, 2, 2.5),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f8fafc',
  },
  sectionCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    border: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e2e8f0'}`,
    padding: theme.spacing(2, 2),
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  },
  sectionTitle: {
    fontSize: '0.8125rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.75),
    paddingLeft: theme.spacing(1.25),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
  field: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': { borderRadius: 10 },
  },
  dialogActions: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  btn: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    minWidth: 100,
  },
}));

export default function InternalChatWidget({ disabled = false }) {
  const classes = useStyles();
  const createDialogClasses = useCreateChatDialogStyles();
  const { user, socket } = useContext(AuthContext);
  const { datetimeToClient } = useDate();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [chats, setChats] = useState([]);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [_messagesPageInfo, setMessagesPageInfo] = useState({ hasMore: false });
  const [messagesPage, setMessagesPage] = useState(1);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createTitleError, setCreateTitleError] = useState('');
  const [createUsers, setCreateUsers] = useState([]);
  const [pendingAttachmentFile, setPendingAttachmentFile] = useState(null);
  const [attachmentPreviewModalOpen, setAttachmentPreviewModalOpen] = useState(false);
  const [deleteMessageModalOpen, setDeleteMessageModalOpen] = useState(false);
  const [pendingDeleteMessageId, setPendingDeleteMessageId] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [confirmDeleteConversationOpen, setConfirmDeleteConversationOpen] = useState(false);
  const [chatPendingDelete, setChatPendingDelete] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalChat, setEditModalChat] = useState(null);
  const messagesBaseRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const messageScrollTailRef = useRef({ len: 0, lastId: null });
  const widgetFileInputRef = useRef(null);
  const composerInputElRef = useRef(null);
  const shouldRestoreComposerFocusRef = useRef(false);
  const messageInputRef = useRef('');

  const postChatMessage = useCallback(
    async (content, file) => {
      if ((!content && !file) || !currentChat?.id || sendingMessage) return;
      shouldRestoreComposerFocusRef.current = true;
      setSendingMessage(true);
      try {
        if (file) {
          const fd = new FormData();
          fd.append('message', content);
          fd.append('medias', file, file.name);
          await api.post(`/chats/${currentChat.id}/messages`, fd);
        } else {
          await api.post(`/chats/${currentChat.id}/messages`, { message: content });
        }
        setMessageInput('');
        setPendingAttachmentFile(null);
        if (widgetFileInputRef.current) widgetFileInputRef.current.value = '';
      } catch (err) {
        toastError(err);
      } finally {
        setSendingMessage(false);
      }
    },
    [currentChat?.id, sendingMessage]
  );

  useEffect(() => {
    messageInputRef.current = messageInput;
  }, [messageInput]);

  const { isRecording, recordingSeconds, startRecording, confirmRecording, cancelRecording } =
    useInternalChatVoiceRecorder({
      onFile: (file) => {
        postChatMessage(messageInputRef.current.trim(), file);
      },
      onError: (err) => {
        if (err?.message === 'unsupported') {
          toast.info(i18n.t('chatInternal.media.recordUnsupported'));
        } else {
          toast.error(i18n.t('chatInternal.media.recordError'));
        }
      },
    });

  useEffect(() => {
    if (!pendingAttachmentFile) setAttachmentPreviewModalOpen(false);
  }, [pendingAttachmentFile]);

  useEffect(() => {
    if (!shouldRestoreComposerFocusRef.current) return;
    if (sendingMessage || loadingMessages) return;
    if (!currentChat?.id) {
      shouldRestoreComposerFocusRef.current = false;
      return;
    }
    requestAnimationFrame(() => composerInputElRef.current?.focus?.());
    shouldRestoreComposerFocusRef.current = false;
  }, [sendingMessage, loadingMessages, currentChat?.id]);

  const handleStartVoice = () => {
    setPendingAttachmentFile(null);
    if (widgetFileInputRef.current) widgetFileInputRef.current.value = '';
    startRecording();
  };

  const sendMessage = () => postChatMessage(messageInput.trim(), pendingAttachmentFile);

  const requestDeleteMessage = useCallback(
    (messageId) => {
      if (!currentChat?.id || messageId == null) return;
      setPendingDeleteMessageId(messageId);
      setDeleteMessageModalOpen(true);
    },
    [currentChat?.id]
  );

  const handleDeleteMessageModalClose = useCallback(() => {
    setDeleteMessageModalOpen(false);
    setPendingDeleteMessageId(null);
  }, []);

  const confirmDeleteMessage = useCallback(async () => {
    const messageId = pendingDeleteMessageId;
    if (!currentChat?.id || messageId == null) {
      setDeleteMessageModalOpen(false);
      setPendingDeleteMessageId(null);
      return;
    }
    if (sendingMessage) return;
    setDeleteMessageModalOpen(false);
    setPendingDeleteMessageId(null);
    try {
      await api.delete(`/chats/${currentChat.id}/messages/${messageId}`);
      setMessages((prev) => prev.filter((m) => String(m.id) !== String(messageId)));
    } catch (err) {
      toastError(err);
    }
  }, [currentChat?.id, pendingDeleteMessageId, sendingMessage]);

  const filteredChats = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return chats;
    return chats.filter((c) =>
      `${c.title || ''} ${c.lastMessage || ''}`.toLowerCase().includes(term)
    );
  }, [search, chats]);

  const unreadsCount = useMemo(
    () => sumInternalChatUnreadsForUser(chats, user?.id),
    [chats, user?.id]
  );

  const scrollToBottom = useCallback(() => {
    const el = messagesAreaRef.current;
    if (!el) {
      messagesBaseRef.current?.scrollIntoView({ block: 'end', behavior: 'auto' });
      return;
    }
    const run = () => {
      el.scrollTop = el.scrollHeight;
    };
    run();
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  }, []);

  useLayoutEffect(() => {
    messageScrollTailRef.current = { len: 0, lastId: null };
  }, [currentChat?.id]);

  useLayoutEffect(() => {
    const list = Array.isArray(messages) ? messages : [];
    const len = list.length;
    const lastId = len > 0 ? list[len - 1]?.id : null;
    const prev = messageScrollTailRef.current;
    const grew = len > prev.len;
    const lastChanged = lastId != null && lastId !== prev.lastId;
    if (grew && lastChanged) {
      scrollToBottom();
    }
    messageScrollTailRef.current = { len, lastId };
  }, [messages, scrollToBottom]);

  // Ao reabrir o widget dentro de um chat já selecionado, mantém a visão nas últimas mensagens.
  useEffect(() => {
    if (!open || !currentChat?.id) return;
    const timers = [];
    requestAnimationFrame(() => scrollToBottom());
    // Reforço em pequenos atrasos: cobre mídia/layout tardio e garante fundo real da conversa.
    [80, 220, 420].forEach((ms) => {
      const id = setTimeout(() => scrollToBottom(), ms);
      timers.push(id);
    });
    return () => {
      timers.forEach((id) => clearTimeout(id));
    };
  }, [open, currentChat?.id, scrollToBottom]);

  const loadChats = async () => {
    try {
      const { data } = await api.get('/chats');
      const list = Array.isArray(data?.records) ? data.records : [];
      setChats(list);
      setCurrentChat((cur) => {
        if (!cur?.id) return cur;
        const updated = list.find((c) => String(c.id) === String(cur.id));
        return updated ? { ...updated } : cur;
      });
    } catch (err) {
      toastError(err);
    }
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditModalChat(null);
    loadChats();
  };

  const handleConfirmDeleteConversation = async () => {
    const chat = chatPendingDelete;
    setChatPendingDelete(null);
    if (!chat?.id) return;
    try {
      await api.delete(`/chats/${chat.id}`);
      setChats((prev) => prev.filter((c) => String(c.id) !== String(chat.id)));
      if (currentChat?.id && String(currentChat.id) === String(chat.id)) {
        setPendingAttachmentFile(null);
        if (widgetFileInputRef.current) widgetFileInputRef.current.value = '';
        setCurrentChat(null);
        setMessages([]);
        setMessagesPage(1);
        setMessageInput('');
      }
    } catch (err) {
      toastError(err);
    }
  };

  const loadMessages = async (chatId, reset = true) => {
    if (!chatId) return;
    try {
      setLoadingMessages(true);
      const nextPage = reset ? 1 : messagesPage;
      const { data } = await api.get(`/chats/${chatId}/messages?pageNumber=${nextPage}`);
      setMessagesPageInfo(data || { hasMore: false });
      if (reset) {
        setMessages(Array.isArray(data?.records) ? data.records : []);
        setMessagesPage(2);
      } else {
        setMessages((prev) => [...(Array.isArray(data?.records) ? data.records : []), ...prev]);
        setMessagesPage((prev) => prev + 1);
      }
      if (reset) {
        setTimeout(scrollToBottom, 120);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const markRead = async (chat) => {
    if (!chat?.id) return;
    try {
      await api.post(`/chats/${chat.id}/read`);
      setChats((prev) =>
        prev.map((c) => {
          if (String(c.id) !== String(chat.id) || !Array.isArray(c.users)) return c;
          return {
            ...c,
            users: c.users.map((u) =>
              String(u.userId) === String(user.id) ? { ...u, unreads: 0 } : u
            ),
          };
        })
      );
    } catch (_) {
      // silent
    }
  };

  const selectChat = async (chat) => {
    if (!chat?.id) return;
    setPendingAttachmentFile(null);
    if (widgetFileInputRef.current) widgetFileInputRef.current.value = '';
    setCurrentChat(chat);
    setMessages([]);
    setMessagesPage(1);
    await loadMessages(chat.id, true);
    await markRead(chat);
  };

  const handleBackToList = () => {
    setPendingAttachmentFile(null);
    if (widgetFileInputRef.current) widgetFileInputRef.current.value = '';
    setCurrentChat(null);
    setMessages([]);
    setMessagesPage(1);
    setMessageInput('');
  };

  const createChat = async () => {
    if (!Array.isArray(createUsers) || createUsers.length === 0) return;
    const trimmed = (createTitle || '').trim();
    if (!trimmed) {
      setCreateTitleError(i18n.t('chatInternal.modal.titleRequired'));
      return;
    }
    setCreateTitleError('');
    try {
      const { data } = await api.post('/chats', {
        title: trimmed,
        users: createUsers,
      });
      setCreateDialogOpen(false);
      setCreateTitle('');
      setCreateTitleError('');
      setCreateUsers([]);
      await loadChats();
      if (data?.id) {
        await selectChat(data);
      }
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    if (open) {
      loadChats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!user?.companyId || !socket?.on || !socket?.off) return;

    const companyId = user.companyId;

    const onChatUser = (data) => {
      if (data?.action === 'create' && data?.record) {
        setChats((prev) => upsertInternalChatInList(prev, data.record));
      }
      if (data?.action === 'update' && data?.record) {
        setChats((prev) => upsertInternalChatInList(prev, data.record));
        setCurrentChat((cur) =>
          cur?.id != null && String(cur.id) === String(data.record.id)
            ? { ...cur, ...data.record }
            : cur
        );
      }
    };

    const applyDeleteMessageFromSocket = (data) => {
      if (data?.action !== 'delete-message' || data.messageId == null) return;
      const messageId = data.messageId;
      const chatPayload = data.chat;
      if (chatPayload?.id != null) {
        setChats((prev) => upsertInternalChatInList(prev, chatPayload));
        setCurrentChat((cur) =>
          cur?.id != null && String(cur.id) === String(chatPayload.id) ? { ...chatPayload } : cur
        );
      }
      if (
        currentChat?.id != null &&
        chatPayload?.id != null &&
        String(chatPayload.id) === String(currentChat.id)
      ) {
        setMessages((prev) => prev.filter((m) => String(m.id) !== String(messageId)));
      }
    };

    const onCompanyChat = (data) => {
      if (data?.action === 'delete' && data?.id != null) {
        const delId = data.id;
        setChats((prev) => prev.filter((c) => String(c.id) !== String(delId)));
        if (currentChat?.id != null && String(currentChat.id) === String(delId)) {
          setCurrentChat(null);
          setMessages([]);
        }
      }
      if ((data?.action === 'new-message' || data?.action === 'update') && data?.chat) {
        setChats((prev) => upsertInternalChatInList(prev, data.chat));
        setCurrentChat((cur) =>
          cur?.id != null && String(cur.id) === String(data.chat.id) ? { ...cur, ...data.chat } : cur
        );
        if (
          data.action === 'new-message' &&
          data.newMessage &&
          String(data.newMessage.senderId) !== String(user.id) &&
          String(data.chat.id) !== String(currentChat?.id)
        ) {
          playInternalChatNotificationSound();
        }
      }
      applyDeleteMessageFromSocket(data);
    };

    const onCurrentChat = (data) => {
      if (!currentChat?.id) return;
      if (data?.action === 'new-message' && data?.newMessage) {
        setMessages((prev) => [...prev, data.newMessage]);
        if (data?.chat) {
          setChats((prev) => upsertInternalChatInList(prev, data.chat));
          setCurrentChat((cur) =>
            String(cur.id) === String(data.chat.id) ? { ...cur, ...data.chat } : cur
          );
        }
        if (String(data.newMessage.senderId) !== String(user.id)) {
          playInternalChatNotificationSound();
        }
        setTimeout(scrollToBottom, 80);
      }
      if (data?.action === 'update' && data?.chat) {
        setChats((prev) => upsertInternalChatInList(prev, data.chat));
        setCurrentChat((cur) =>
          String(cur.id) === String(data.chat.id) ? { ...cur, ...data.chat } : cur
        );
      }
      applyDeleteMessageFromSocket(data);
    };

    socket.on(`company-${companyId}-chat-user-${user.id}`, onChatUser);
    socket.on(`company-${companyId}-chat`, onCompanyChat);
    if (currentChat?.id) {
      socket.on(`company-${companyId}-chat-${currentChat.id}`, onCurrentChat);
    }

    return () => {
      socket.off(`company-${companyId}-chat-user-${user.id}`, onChatUser);
      socket.off(`company-${companyId}-chat`, onCompanyChat);
      if (currentChat?.id) {
        socket.off(`company-${companyId}-chat-${currentChat.id}`, onCurrentChat);
      }
    };
  }, [socket, user, currentChat]);

  const pendingDeletePreview = useMemo(() => {
    const m = messages.find((x) => String(x.id) === String(pendingDeleteMessageId));
    if (!m) return '';
    return getInternalChatDeleteMessagePreview(m, {
      audioLabel: i18n.t('chatInternal.deleteModal.previewAudio'),
      imageLabel: i18n.t('chatInternal.deleteModal.previewImage'),
      videoLabel: i18n.t('chatInternal.deleteModal.previewVideo'),
      documentLabel: i18n.t('chatInternal.deleteModal.previewDocument'),
    });
  }, [messages, pendingDeleteMessageId]);

  const threadSubtitle = useMemo(() => {
    if (!currentChat) return '';
    const plain = stripInternalChatMessageCaption(currentChat.lastMessage || '').replace(
      /\s+/g,
      ' '
    );
    const t = plain.trim();
    if (t.length > 88) return `${t.slice(0, 88)}…`;
    return t;
  }, [currentChat]);

  if (disabled) return null;

  return (
    <>
      <DeleteConfirmModal
        open={deleteMessageModalOpen}
        onClose={handleDeleteMessageModalClose}
        onConfirm={confirmDeleteMessage}
        messageText={pendingDeletePreview}
      />
      <ConfirmationModal
        title="Excluir conversa"
        open={confirmDeleteConversationOpen}
        onClose={() => setConfirmDeleteConversationOpen(false)}
        onConfirm={handleConfirmDeleteConversation}
      >
        Esta ação não pode ser revertida. Confirmar exclusão?
      </ConfirmationModal>
      {editModalOpen && editModalChat ? (
        <ChatModal
          open={editModalOpen}
          chat={editModalChat}
          type="edit"
          handleClose={closeEditModal}
          handleLoadNewChat={() => {}}
        />
      ) : null}
      <InternalChatAttachmentPreviewModal
        open={attachmentPreviewModalOpen}
        file={pendingAttachmentFile}
        onDiscard={() => {
          setPendingAttachmentFile(null);
          setAttachmentPreviewModalOpen(false);
          if (widgetFileInputRef.current) widgetFileInputRef.current.value = '';
        }}
        onSend={() => {
          sendMessage();
        }}
        sendDisabled={!currentChat || loadingMessages || sendingMessage}
      />
      <Fab
        color="primary"
        className={`${classes.fab} ${open ? classes.fabOpen : classes.fabClosed}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-label={
          open ? i18n.t('chatInternal.modal.cancel') : i18n.t('chatInternal.widget.hubKicker')
        }
      >
        {!open && unreadsCount > 0 ? (
          <Box component="span" className={classes.fabUnreadBadge} aria-label={String(unreadsCount)}>
            {unreadsCount > 99 ? '99+' : unreadsCount}
          </Box>
        ) : null}
        {open ? <CloseIcon /> : <ForumIcon />}
      </Fab>

      <Backdrop
        className={classes.widgetBackdrop}
        open={open}
        onClick={() => setOpen(false)}
        aria-hidden
      />

      {open && (
        <Box className={classes.panelRoot}>
          {!currentChat ? (
            <>
              <div className={classes.hubHeader}>
                <div className={classes.hubHeaderTop}>
                  <div className={classes.hubKicker}>
                    <span className={classes.statusDot} aria-hidden />
                    {i18n.t('chatInternal.widget.hubKicker')}
                  </div>
                  <div className={classes.hubHeaderActions}>
                    <Tooltip title={i18n.t('chatInternal.modal.cancel')}>
                      <IconButton
                        size="small"
                        className={classes.iconOnHeader}
                        onClick={() => setOpen(false)}
                        aria-label={i18n.t('chatInternal.modal.cancel')}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>
              <Box className={classes.searchRow}>
                <TextField
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={i18n.t('chatInternal.widget.searchPlaceholder')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{ notched: false }}
                  className={classes.searchField}
                />
              </Box>
              <div className={classes.listArea}>
                {filteredChats.map((chat) => {
                  const unread = getInternalChatUnreadForUser(chat, user?.id);
                  const preview =
                    stripInternalChatMessageCaption(chat.lastMessage || '').trim() ||
                    datetimeToClient(chat.updatedAt);
                  const isOwner = isInternalChatOwner(chat, user?.id);
                  return (
                    <Box key={chat.id} className={classes.chatRowOuter}>
                      <Box
                        component="button"
                        type="button"
                        className={classes.chatRow}
                        onClick={() => selectChat(chat)}
                      >
                        <Box className={classes.chatRowAvatar} aria-hidden>
                          <ForumIcon />
                        </Box>
                        <Box className={classes.chatRowBody}>
                          <Typography className={classes.chatRowTitle}>
                            {chat.title || `Chat ${chat.id}`}
                          </Typography>
                          <Typography className={classes.chatRowPreview}>{preview}</Typography>
                        </Box>
                        <Box className={classes.chatRowMeta}>
                          <Typography className={classes.chatRowDate}>
                            {datetimeToClient(chat.updatedAt)}
                          </Typography>
                          {unread > 0 ? (
                            <Box
                              component="span"
                              className={classes.unreadCount}
                              aria-label={String(unread)}
                            >
                              {unread > 99 ? '99+' : unread}
                            </Box>
                          ) : null}
                        </Box>
                      </Box>
                      {isOwner ? (
                        <div
                          className={classes.chatRowActions}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          role="presentation"
                        >
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              className={classes.chatRowActionBtn}
                              aria-label="Editar conversa"
                              onClick={() => {
                                setEditModalChat(chat);
                                setEditModalOpen(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              className={`${classes.chatRowActionBtn} ${classes.chatRowActionBtnDanger}`}
                              aria-label="Excluir conversa"
                              onClick={() => {
                                setChatPendingDelete(chat);
                                setConfirmDeleteConversationOpen(true);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      ) : null}
                    </Box>
                  );
                })}
                {filteredChats.length === 0 ? (
                  <div className={classes.emptyState}>
                    {i18n.t('chatInternal.widget.emptyList')}
                  </div>
                ) : null}
              </div>
              <Box className={classes.newChatCtaWrap}>
                <Button
                  fullWidth
                  color="primary"
                  variant="outlined"
                  className={classes.newChatCta}
                  startIcon={<AddIcon style={{ fontSize: 20 }} />}
                  onClick={() => {
                    setCreateTitleError('');
                    setCreateDialogOpen(true);
                  }}
                >
                  {i18n.t('chatInternal.widget.newConversation')}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <div className={classes.threadHeader}>
                <Tooltip title={i18n.t('chatInternal.header.back')}>
                  <IconButton
                    size="small"
                    className={classes.iconOnHeader}
                    onClick={handleBackToList}
                    aria-label={i18n.t('chatInternal.header.back')}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Box className={classes.threadHeaderText}>
                  <Typography className={classes.threadTitle}>
                    {currentChat.title || `Chat ${currentChat.id}`}
                  </Typography>
                  <Typography className={classes.threadSubtitle}>
                    {threadSubtitle ||
                      (!loadingMessages && messages.length === 0
                        ? i18n.t('chatInternal.widget.emptyThread')
                        : '')}
                  </Typography>
                </Box>
                <Tooltip title={i18n.t('chatInternal.modal.cancel')}>
                  <IconButton
                    size="small"
                    className={classes.iconOnHeader}
                    onClick={() => setOpen(false)}
                    aria-label={i18n.t('chatInternal.modal.cancel')}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
              <div ref={messagesAreaRef} className={classes.messagesArea}>
                {messages.length === 0 && !loadingMessages ? (
                  <div className={classes.emptyState}>
                    {i18n.t('chatInternal.widget.emptyThread')}
                  </div>
                ) : null}
                {messages.map((item, index) => {
                  const mine = item?.senderId === user?.id;
                  const html = item?.message || '';
                  const plainCaption = stripInternalChatMessageCaption(html);
                  const rawMedia = item?.mediaPath || item?.mediaUrl;
                  const hasMedia = Boolean(rawMedia);
                  const mediaKind = hasMedia
                    ? getInternalChatMediaKind(rawMedia, item.mediaName)
                    : null;
                  const isAudio = mediaKind === 'audio';
                  const hasNonAudioMedia = hasMedia && !isAudio;
                  const hasCaption = Boolean(plainCaption);
                  const audioOnly = isAudio && !hasCaption && !hasNonAudioMedia;
                  const showBubble =
                    hasNonAudioMedia ||
                    (!hasMedia && hasCaption) ||
                    (hasMedia && hasCaption) ||
                    (!mine && !audioOnly);

                  return (
                    <Box key={`${item.id || index}-${item.createdAt}`}>
                      <Box
                        className={`${classes.messageStack} ${
                          mine ? classes.messageStackMine : classes.messageStackTheirs
                        }`}
                      >
                        {!mine && audioOnly && (item?.sender?.name || 'Usuário') ? (
                          <Typography component="span" className={classes.senderAboveVoice}>
                            {item?.sender?.name || 'Usuário'}
                          </Typography>
                        ) : null}
                        {showBubble ? (
                          <Box className={mine ? classes.messageRight : classes.messageLeft}>
                            {!mine && !audioOnly ? (
                              <Typography component="span" className={classes.messageSenderName}>
                                {item?.sender?.name || 'Usuário'}
                              </Typography>
                            ) : null}
                            {hasMedia && plainCaption ? (
                              <Typography component="span" className={classes.messageCaptionPlain}>
                                {plainCaption}
                              </Typography>
                            ) : null}
                            {!hasMedia && plainCaption ? (
                              <div
                                className={classes.messageBody}
                                dangerouslySetInnerHTML={{ __html: html }}
                              />
                            ) : null}
                            {hasNonAudioMedia ? (
                              <InternalChatMessageMedia
                                mediaPath={rawMedia}
                                mediaName={item.mediaName}
                                bubbleTone={mine ? 'outgoing' : 'incoming'}
                              />
                            ) : null}
                            {!isAudio ? (
                              <Box className={classes.messageTimeRow}>
                                <Typography
                                  component="span"
                                  className={classes.messageTime}
                                  style={{ display: 'inline', marginTop: 0 }}
                                >
                                  {datetimeToClient(item?.createdAt)}
                                </Typography>
                                {mine && item.id != null ? (
                                  <Tooltip title={i18n.t('chatInternal.deleteMessage')}>
                                    <IconButton
                                      size="small"
                                      className={classes.messageDeleteIcon}
                                      aria-label={i18n.t('chatInternal.deleteMessage')}
                                      onClick={() => requestDeleteMessage(item.id)}
                                      disabled={sendingMessage}
                                    >
                                      <DeleteOutlineIcon style={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                              </Box>
                            ) : null}
                          </Box>
                        ) : null}
                        {isAudio ? (
                          <InternalChatVoiceNote
                            mediaPath={rawMedia}
                            mediaName={item.mediaName}
                            speakerName={mine ? user?.name : item?.sender?.name}
                            mine={mine}
                            sender={item?.sender}
                          />
                        ) : null}
                        {isAudio ? (
                          <Box
                            component="div"
                            className={`${classes.timeBelowVoice} ${
                              mine ? classes.timeBelowVoiceMine : classes.timeBelowVoiceTheirs
                            }`}
                          >
                            <Typography
                              component="span"
                              variant="inherit"
                              style={{ fontSize: 'inherit', color: 'inherit' }}
                            >
                              {datetimeToClient(item?.createdAt)}
                            </Typography>
                            {mine && item.id != null ? (
                              <Tooltip title={i18n.t('chatInternal.deleteMessage')}>
                                <IconButton
                                  size="small"
                                  className={classes.messageDeleteIcon}
                                  aria-label={i18n.t('chatInternal.deleteMessage')}
                                  onClick={() => requestDeleteMessage(item.id)}
                                  disabled={sendingMessage}
                                >
                                  <DeleteOutlineIcon style={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  );
                })}
                <div ref={messagesBaseRef} />
              </div>

              <div className={classes.composer}>
                <input
                  ref={widgetFileInputRef}
                  type="file"
                  hidden
                  accept="image/*,video/*,audio/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setPendingAttachmentFile(f);
                      setAttachmentPreviewModalOpen(true);
                    }
                  }}
                />
                <div className={classes.composerRow}>
                  {!isRecording ? (
                    <Tooltip title={i18n.t('chatInternal.media.attach')}>
                      <IconButton
                        size="small"
                        onClick={() => widgetFileInputRef.current?.click()}
                        disabled={!currentChat || loadingMessages || sendingMessage}
                        aria-label={i18n.t('chatInternal.media.attach')}
                        className={classes.actionIconMuted}
                      >
                        <AttachFileIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  <Box className={classes.composerInputShell}>
                    {isRecording ? (
                      <TextField
                        value=""
                        placeholder={i18n.t('chatInternal.media.recordingPlaceholder')}
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled
                        className={classes.composerTextField}
                        InputProps={{ readOnly: true, notched: false }}
                      />
                    ) : (
                      <TextField
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                          }
                        }}
                        placeholder={i18n.t('chatInternal.widget.typeMessage')}
                        variant="outlined"
                        size="small"
                        multiline
                        minRows={1}
                        maxRows={6}
                        fullWidth
                        disabled={!currentChat}
                        className={classes.composerTextField}
                        inputRef={composerInputElRef}
                        InputProps={{ notched: false }}
                      />
                    )}
                  </Box>
                  {isRecording ? (
                    <Box className={classes.recordingChronoOutside} aria-live="polite">
                      <span className={classes.recordingDot} aria-hidden />
                      <Typography component="span" className={classes.recordingTime}>
                        {formatRecordingClock(recordingSeconds)}
                      </Typography>
                    </Box>
                  ) : null}
                  <Box className={classes.recorderActions}>
                    {isRecording ? (
                      <>
                        <Tooltip title={i18n.t('chatInternal.media.recordCancel')}>
                          <IconButton
                            size="small"
                            onClick={cancelRecording}
                            disabled={!currentChat || loadingMessages || sendingMessage}
                            aria-label={i18n.t('chatInternal.media.recordCancel')}
                          >
                            <HighlightOffIcon
                              className={classes.cancelRecordIcon}
                              fontSize="small"
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={i18n.t('chatInternal.media.recordConfirm')}>
                          <IconButton
                            size="small"
                            onClick={confirmRecording}
                            disabled={!currentChat || loadingMessages || sendingMessage}
                            aria-label={i18n.t('chatInternal.media.recordConfirm')}
                          >
                            <CheckCircleOutlineIcon
                              className={classes.confirmRecordIcon}
                              fontSize="small"
                            />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : messageInput.trim() || pendingAttachmentFile ? (
                      <Tooltip title={i18n.t('chatInternal.media.send')}>
                        <span className={classes.composerIconWrap}>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={sendMessage}
                            disabled={
                              !currentChat ||
                              sendingMessage ||
                              loadingMessages ||
                              (!messageInput.trim() && !pendingAttachmentFile) ||
                              attachmentPreviewModalOpen
                            }
                            aria-label={i18n.t('chatInternal.media.send')}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={i18n.t('chatInternal.media.recordAudio')}>
                        <span className={classes.composerIconWrap}>
                          <IconButton
                            size="small"
                            onClick={handleStartVoice}
                            disabled={!currentChat || loadingMessages || sendingMessage}
                            aria-label={i18n.t('chatInternal.media.recordAudio')}
                            className={classes.actionIconMuted}
                          >
                            <MicIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </div>
              </div>
            </>
          )}
        </Box>
      )}

      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateTitleError('');
          setCreateDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        aria-labelledby="widget-create-chat-title"
        PaperProps={{ className: createDialogClasses.dialogPaper }}
      >
        <Box className={createDialogClasses.dialogHeader} component="header">
          <Box className={createDialogClasses.headerLeft}>
            <Box className={createDialogClasses.headerIcon} aria-hidden>
              <ForumIcon />
            </Box>
            <Box minWidth={0}>
              <Typography
                component="h2"
                className={createDialogClasses.headerTitle}
                id="widget-create-chat-title"
              >
                Novo chat interno
              </Typography>
              <Typography className={createDialogClasses.headerSubtitle}>
                Defina um título e escolha os participantes do chat.
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Fechar">
            <IconButton
              size="small"
              onClick={() => {
                setCreateTitleError('');
                setCreateDialogOpen(false);
              }}
              className={createDialogClasses.headerCloseBtn}
              aria-label="Fechar"
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <DialogContent className={createDialogClasses.dialogContent}>
          <Box className={createDialogClasses.sectionCard}>
            <Typography component="h3" className={createDialogClasses.sectionTitle}>
              Dados da conversa
            </Typography>
            <TextField
              value={createTitle}
              onChange={(e) => {
                setCreateTitle(e.target.value);
                if (createTitleError) setCreateTitleError('');
              }}
              label="Título do chat"
              placeholder="Ex.: Financeiro e suporte"
              variant="outlined"
              size="small"
              fullWidth
              className={createDialogClasses.field}
              error={Boolean(createTitleError)}
              helperText={createTitleError || undefined}
            />
            <UsersFilter initialUsers={createUsers} onFiltered={setCreateUsers} />
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ display: 'block', marginTop: 8 }}
            >
              Selecione ao menos um participante para criar o chat.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions className={createDialogClasses.dialogActions}>
          <Button
            className={createDialogClasses.btn}
            onClick={() => {
              setCreateTitleError('');
              setCreateDialogOpen(false);
            }}
          >
            Cancelar
          </Button>
          <Button
            className={createDialogClasses.btn}
            onClick={createChat}
            color="primary"
            variant="contained"
            disabled={!Array.isArray(createUsers) || createUsers.length === 0}
          >
            Criar chat
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
